import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MvsFormFieldBaseComponent} from "../mvs-form-field-base/mvs-form-field-base.component";
import {MvsFormFieldChange} from "../../mvs-form-field-change";
import {MvsForwardAction} from "../../mvs-forward-action";
import {MvsFormFieldDto} from "../../../dto/form/dto/mvs-form-field.dto";
import {FormBuilder, FormControl} from "@angular/forms";
import {MvsCoreService} from "../../../service/mvs-core.service";
import {FieldStorageService} from "../../field/service/field-storage.service";


/**
 * This component is used to display a FormField based on the provided information within "formField".
 * The attribute "readonly" is used to change to the uiOutputControl while the disabled attribute is
 * used to simply disable the form field for data entries (e.g. during busy state).
 *
 * The action "onChange" is raised whenever the field value changed. Field changes to input like UI
 * controls will be delayed by "defaultDebounceTime" seconds. This mechanism is used to prevent
 * emitting too many "onChange" events.
 *
 */
@Component({
    selector: 'mvs-form-field-wrapper',
    template: `
      @if (initialized) {
        <mvs-form-field
            [formField]="formFieldClone"
            [formGroup]="formGroup"
            [readOnly]="readOnly"
            [disabled]="disabled"
            [hideLabel]="hideLabel"
            (onChange)="handleOnChange($event)"
            (onForwardAction)="handleForwardAction($event)"
        ></mvs-form-field>
      }`,
    standalone: false
})
export class MvsFormFieldWrapperComponent extends MvsFormFieldBaseComponent {

  /**
   * Name of the control this wrapper renders, inside the form group it was given or built.
   *
   * <p>BOUND AS {@code [controlName]}, and the alias is not cosmetic. Angular's own
   * {@code FormControlName} directive (ReactiveFormsModule, imported AND exported by SharedModule)
   * has the selector {@code [formControlName]} — so every template binding an input of that NAME,
   * with or without brackets, ALSO instantiates that directive on the element. It then demands a
   * parent {@code formGroup} / {@code formGroupName} / {@code formArrayName} container, which this
   * wrapper never provides: it owns a private FormGroup of its own and hands it to the inner control
   * as a plain input. Without the container it raises NG01050 in dev mode and dereferences a null
   * form directive in production, on the very change-detection pass that creates the editor.
   *
   * <p>The property keeps its name so nothing inside the component changes; only the template-facing
   * name moves out of Angular's selector.
   */
  @Input('controlName') formControlName: string;
  @Input() value: any;

  // TICKET: ENH1000001257
  // Added output to emit the actual field value when it changes.
  // Reason: The existing onChange event only emits MvsFormFieldChange which contains
  // field metadata (MvsFormFieldDto) but NOT the actual value. Parents using this wrapper
  // had no way to retrieve the new value since the internal formGroup is encapsulated.
  @Output() onValueChange = new EventEmitter<any>();

  formFieldClone: MvsFormFieldDto;

  constructor(protected formBuilder: FormBuilder,
              protected coreService: MvsCoreService,
              protected fieldStorageService: FieldStorageService) {
    super(coreService, fieldStorageService);
  }


  override ngOnInit() {
    super.ngOnInit();

    // If parent DID NOT provide formGroup → build one
    if (!this.formGroup) {
      (this.formGroup as any) = this.formBuilder.group({
        [this.formControlName]: new FormControl(this.value ?? null)
      });

    }

    this.refreshComponent();
  }


  override refreshComponent() {

    this.formFieldClone = Object.assign({}, this.formField);
    this.formFieldClone.id = this.formControlName;
    this.formFieldClone.access = 'update'
    this.initialized = true;

  }

  // TICKET: ENH1000001257
  // Extract value from internal formGroup and emit via onValueChange.
  // This allows parent components to receive the actual value for saving.
  handleOnChange(event: MvsFormFieldChange) {
    const newValue = this.formGroup.get(this.formControlName)?.value;
    this.onValueChange.emit(newValue);
    this.onChange.emit(event);
  }

  handleForwardAction(event: MvsForwardAction) {
    this.onForwardAction.emit(event);
  }


}
